import { AppUtils_getLocationFragment$key } from "@/apps/util/__generated__/AppUtils_getLocationFragment.graphql"
import ROUTE_NAMES from "@/core/route/util/routeNames"
import generateCoursePaths from "@/organization/common/sidebar/my-experiences-list/util/generateCoursePaths"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import { LocationDescriptor } from "history"
import { generatePath } from "react-router-dom"
import { ConnectionHandler, graphql, readInlineData } from "relay-runtime"

namespace AppUtils {
  export function getFeedConnections(
    organizationId: GlobalID | null | undefined,
    productId: GlobalID | null | undefined
  ) {
    const connections = []

    if (organizationId) {
      connections.push(
        ConnectionHandler.getConnectionID(
          organizationId,
          "OrganizationFeedSelectorDropdown_feeds"
        )
      )
    }

    if (productId) {
      connections.push(
        ConnectionHandler.getConnectionID(productId, "ProductFeedSelectorDropdown_feeds")
      )
    }
    return connections
  }

  export function getAppConnectionIds(
    organizationId: GlobalID,
    productId: GlobalID | null | undefined,
    navSectionId: GlobalID | null | undefined
  ) {
    if (productId)
      return [
        ConnectionHandler.getConnectionID(
          productId,
          "AppsSidebarList_ProductFragment__productApps"
        ),
      ]
    if (navSectionId)
      return [
        ConnectionHandler.getConnectionID(
          navSectionId,
          "AppsSidebarList_NavSectionFragment__apps"
        ),
      ]
    return [
      ConnectionHandler.getConnectionID(
        organizationId,
        "AppsSidebarList_OrganizationFragment__apps"
      ),
    ]
  }

  export function getLocation(
    appKey: AppUtils_getLocationFragment$key
  ): LocationDescriptor | null {
    const app = readInlineData<AppUtils_getLocationFragment$key>(
      graphql`
        fragment AppUtils_getLocationFragment on ProductApp @inline {
          id
          kind
          customAppUrl
          product {
            slug
          }
          feed {
            id
          }
          chatChannel {
            id
          }
          collection {
            id
          }
          contentUsage {
            id
          }
          linkProduct {
            slug
          }
        }
      `,
      appKey
    )
    if (!app) return null
    const COURSE_PATHS = app.product ? generateCoursePaths(app.product.slug) : null

    switch (app.kind) {
      case "chat_channel":
        return app.product
          ? generatePath(ROUTE_NAMES.PRODUCT.CHAT.CHANNEL, {
              productSlug: app.product.slug,
              channelId: app.chatChannel!.id,
            })
          : generatePath(ROUTE_NAMES.COMMUNITY.CHAT.CHANNEL, {
              channelId: app.chatChannel!.id,
            })
      case "collection":
        return app.product
          ? generatePath(ROUTE_NAMES.PRODUCT.COLLECTION.DETAIL, {
              productSlug: app.product.slug,
              collectionId: app.collection!.id,
            })
          : generatePath(ROUTE_NAMES.COMMUNITY.COLLECTION.DETAIL, {
              collectionId: app.collection!.id,
            })
      case "content":
        return {
          search: `u=${encodeURIComponent(Relay.fromGlobalId(app.contentUsage!.id).id)}`,
        }
      case "curriculum":
        return COURSE_PATHS?.CURRICULUM.ROOT ?? null
      case "dashboard":
        return generatePath(ROUTE_NAMES.COMMUNITY.DASHBOARD_APP.DETAIL, { appId: app.id })
      case "events":
        return COURSE_PATHS?.EVENTS ?? null
      case "link":
        return app.customAppUrl
      case "members":
        return COURSE_PATHS?.MEMBERS.LIST ?? null
      case "organization_events":
        return generatePath(ROUTE_NAMES.COMMUNITY.EVENTS.APP, { appId: app.id })
      case "organization_members":
        return ROUTE_NAMES.COMMUNITY.MEMBERS.LIST
      case "posts":
        return app.product
          ? generatePath(ROUTE_NAMES.PRODUCT.FEED.POSTS.LIST, {
              productSlug: app.product.slug,
              feedId: app.feed!.id,
            })
          : generatePath(ROUTE_NAMES.COMMUNITY.FEED.POSTS.LIST, { feedId: app.feed!.id })
      case "product_link":
        return generatePath(ROUTE_NAMES.PRODUCT.DASHBOARD, {
          productSlug: app.linkProduct!.slug,
        })
      case "group_directory":
        return app.product
          ? generatePath(ROUTE_NAMES.PRODUCT.GROUP_DIRECTORY.DETAIL, {
              productSlug: app.product.slug,
              groupDirectoryId: app.id,
            })
          : generatePath(ROUTE_NAMES.COMMUNITY.GROUP_DIRECTORY.DETAIL, {
              groupDirectoryId: app.id,
            })
      case "member_directory":
        return app.product
          ? generatePath(ROUTE_NAMES.PRODUCT.MEMBER_DIRECTORY, {
              productSlug: app.product.slug,
              appId: app.id,
            })
          : generatePath(ROUTE_NAMES.COMMUNITY.MEMBER_DIRECTORY, { appId: app.id })
      // Non-navigable or legacy app kinds
      case "resources":
      case "nav_folder":
      case "slack":
      case "%future added value":
        return null
    }
  }
}

export default AppUtils
